import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { colors, TabBar, Tab, Button, Theme, themeOptions, Popup, Select, Row, Col, ShowPopup, Artwork, SkubotSpinner, MagicIcon } from '@commonsku/styles';
import Files from './Files';
import ReskuSelect from './Select';
import UploadFilesPopup from './UploadFilesPopup';

import { closePopup, createUploadFilesPopup } from '../actions/popup';
import { createAddTemp } from '../actions/temp';
import { createFinalizeAddProductImage, createFinalizeAddItemImage } from '../actions/index';
import { addProof } from '../actions/purchase_order_proof';
import { createFinalizeUploadFile, createLoadFilesList } from '../actions/file';

import { getFolderOptions, getMockupImprintOptions, getProofOptions } from '../selectors/dropdowns';
import { getItemImagesByItemId, getProductImagesByItemId, getFullItem } from '../selectors';
import { getProduct } from '../selectors';
import { getImageSrc } from '../utils';
import { aiMockupAndRewriterEnabled } from '../utils';

import { BASE_ZINDEX } from '../popup-factory';
import { ImageEditorPopup } from './image-editor/ImageEditorPopup';
import { createLoadFoldersList } from '../actions/folders';
import { folderSorter } from '../redux/folders';

import { BulkMockupGeneratorPopup } from './mockup-generator/BulkMockupGeneratorPopup';
import { sendMockupFeedback, persistPendingFeedback, clearPendingFeedback, startBulkMockupGeneration, persistBulkJobItems, pollBulkMockupJob } from '../services/mockupGeneration';
import { getMockupGeneratorItemLocations, isPromoStandardItem } from './mockup-generator/decorationModel';
import { resolveParentContext, MockupStatus } from './mockup-generator/mockupUtils';
import { resumeMockupGeneration, getPendingFeedbackStatus } from './mockup-generator/mockupGenerationFlow';
import { MockupFeedbackBanner } from './mockup-generator/MockupFeedbackBanner';
import './mockup-generator/BulkMockupGeneratorPopup.css';
import { createLoadOrder } from '../actions/order';
import { isPODShop } from './shop/helpers/helpers';

const DEFAULT_SELECTED_TAB_BY_PARENT_TYPE = {
  'JOB': 'project',
  'PURCHASE-ORDER': 'project',
  'SHOP': 'shop',
  'CATALOG': 'client',
};

// CATALOG is UI-only; file APIs store under CLIENT.
const resolveUploadParentType = (parent_type) =>
  parent_type === 'CATALOG' ? 'CLIENT' : parent_type;

class SelectFilePopup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedTab: DEFAULT_SELECTED_TAB_BY_PARENT_TYPE[props.parent_type] ?? 'item',
      folder_id: 'all',
      item_images: props.item_images,
      showEditorPopup: false,
      showUploadPopup: false,
      mockupGeneratorImage: null,
      hoverImage: false,
      mockupStatus: { type: MockupStatus.IDLE, message: null },
      mockupFeedback: null,
      mockupJobId: null,
    };
    _.bindAll(this, ['handleChangeFolder']);
  }

  handleChangeFolder(folder_id) {
    this.setState({ folder_id });
  }

  componentDidMount() {
    if (this.props.parent_type === 'CATALOG') {
      this.initClientCatalogLibrary(this.props.client_id);
    } else {
      this.props.onLoadFoldersList(this.props.job_id, this.props.client_id);
    }
    this.restorePendingFeedback();
    this.resumeMockupIfNeeded();
  }

  /**
   * Catalog has no project/job. Load the client's file library (folders + files).
   */
  initClientCatalogLibrary = (clientId) => {
    this.props.onLoadFoldersList(clientId, clientId, 'CLIENT');
    this.props.onLoadFilesList(clientId, 'CLIENT');
  };

  restorePendingFeedback = () => {
    const pending = getPendingFeedbackStatus(this.props.itemId);
    if (pending) {
      this.setState({ mockupStatus: pending, mockupJobId: pending.jobId ?? null });
    }
  };

  resumeMockupIfNeeded = () => {
    const { itemId, onFinalizeUploadFile, onSelectFile } = this.props;
    resumeMockupGeneration(itemId, {
      onStatusChange: (mockupStatus) => {
        this.setState({ mockupStatus, mockupJobId: mockupStatus.jobId ?? this.state.mockupJobId });
      },
      onFileReady: (file) => {
        onFinalizeUploadFile(file);
        onSelectFile(file);
      },
    });
  };

  componentDidUpdate() {
    const element = document.getElementById('scrollableDiv');
    if (!!element) {
      element.scrollTo(0, 0);
    }
  }

  static getDerivedStateFromProps(
    nextProps,
    prevState,
  ) {
    if (nextProps.length > 0) {
      if (0 === prevState.item_images.length || nextProps.item_images[0].item_image_id !== prevState.item_images[0].item_image_id) {
        return { item_images: nextProps.item_images };
      }
    }
    return null;
  }

  canEditImage = () => {
    return true;
  };

  renderImageEditor = () => {
    if (this.canEditImage()) {
      this.setState({ showEditorPopup: true });
    }
  };

  renderMockupGenerator = () => {
    const { item_images } = this.props;
    if (item_images.length > 0) {
      this.setState({ mockupGeneratorImage: item_images[0] });
    }
  };

  handleMockupFeedback = (isPositive) => {
    this.setState({ mockupFeedback: isPositive });
    sendMockupFeedback(this.state.mockupJobId, isPositive, this.props.itemId);
    if (this.props.itemId) clearPendingFeedback(this.props.itemId);
  };

  /**
   * @param {import('./mockup-generator/BulkMockupGeneratorPopup').BulkMockupGeneratorConfig} config
   */
  handleGenerateMockup = async (config) => {
    const { itemId, job_id, shop_id, onFinalizeUploadFile, onSelectFile } = this.props;
    const { parent_id } = resolveParentContext({ job_id, shop_id, item_id: itemId });

    this.setState({
      mockupGeneratorImage: null,
      mockupStatus: { type: MockupStatus.PROCESSING, message: 'Configuring products...' },
      mockupFeedback: null,
    });

    try {
      const toImageIdMap = (items) => Object.fromEntries(
        items
          .map(item => [item.item_id, item.item_images?.[0]?.item_image_id])
          .filter(([, imageId]) => imageId)
      );

      const { bulk_job_id } = await startBulkMockupGeneration({
        ...config,
        readyItemsWithItemImageId: toImageIdMap(config.readyItems),
        decoratedItemsWithItemImageId: toImageIdMap(config.decoratedItems),
      });

      const allIds = [
        ...config.readyItems.map(i => i.item_id),
        ...config.decoratedItems.map(i => i.item_id),
      ];
      persistBulkJobItems(bulk_job_id, allIds);

      await pollBulkMockupJob({
        bulkJobId: bulk_job_id,
        parentId: parent_id,
        onStatusChange: (status, response) => {
          if (status === 'completed') {
            const itemData = response.item_data_by_item?.[itemId];
            if (itemData?.file) {
              onFinalizeUploadFile(itemData.file);
              onSelectFile(itemData.file);
            }
            if (itemData?.item_image && itemData?.item_images?.length) {
              this.props.onFinalizeItemImages(itemData.item_image, itemData.item_images);
            }
            if (this.props.item?.order_id) {
              this.props.onLoadOrder(this.props.item.order_id);
            }
            persistPendingFeedback(itemId, bulk_job_id);
            this.setState({
              mockupStatus: { type: MockupStatus.SUCCESS, message: null, jobId: bulk_job_id },
              mockupJobId: bulk_job_id,
            });
          } else if (status === 'failed') {
            this.setState({
              mockupStatus: {
                type: MockupStatus.ERROR,
                message: response.error || 'Mockup generation failed. Please try again.',
              },
            });
          }
        },
      });
    } catch (error) {
      this.setState({
        mockupStatus: {
          type: MockupStatus.ERROR,
          message: error.message || 'Failed to start mockup generation. Please try again.',
        },
      });
    }
  };

  getTabs = (parent_type, client_id) => {
    const tabs = client_id ? [
      {
        key: 'client',
        label: 'Client Files'
      },
    ] : [];

    switch (parent_type) {
      case 'CATALOG':
        // catalog only shows client files
        return tabs;
      case 'JOB':
      case 'PURCHASE-ORDER':
        return ([
          {
            key: 'project',
            label: 'Project Files'
          },
          ...tabs,
        ]);
      case 'SHOP':
        return ([
          {
            key: 'shop',
            label: 'Shop Files'
          },
          ...tabs,
        ]);
      default:
        return ([
          {
            key: 'item',
            label: 'Item Files'
          },
          {
            key: 'project',
            label: 'Project Files'
          },
          ...tabs,
        ]);
    }
  };

  renderTabs() {
    const { clientFolders, jobFolders, parent_id, parent_type, client_id, resku, register } = this.props;
    const { selectedTab, folder_id } = this.state;
    const no_upload = register === 'message_file_id' || register === 'collaborate_file_id';
    const uploadParentType = resolveUploadParentType(parent_type);

    let optionFolders = clientFolders;
    if (selectedTab === 'project') {
      optionFolders = jobFolders;
    }

    optionFolders.sort(folderSorter);

    return (
      <Row>
        <Col xs={6}>
          <TabBar>
            {this.getTabs(parent_type, client_id).map((tab, index) => <Tab key={index}
              selected={tab.key === selectedTab}
              onClick={() => { this.setState({ selectedTab: tab.key, folder_id: 'all' }); }}>
              {tab.label}
            </Tab>)}
          </TabBar>
        </Col>
        <Col xs={4}>
          {(selectedTab === 'client' || (selectedTab === 'project' && resku)) ?
            <ReskuSelect
              options={optionFolders}
              value={folder_id}
              change={this.handleChangeFolder}
              style={{ width: '150px', float: 'right', 'zIndex': 1008, marginTop: '10px' }}
            />
            : (selectedTab === 'client' || selectedTab === 'project') ?
              <div style={{ marginTop: '10px' }}>
                <Select
                  options={optionFolders}
                  value={optionFolders.filter(f => f.value === folder_id)}
                  onChange={option => this.handleChangeFolder(option.value)}
                />
              </div>
              : null}
        </Col>
        <Col xs={2}>
          {!no_upload && resku ? <Button
            style={{ float: 'right' }}
            secondary
            onClick={() => this.setState({ showUploadPopup: true })}>
            Upload
          </Button>
            : (!no_upload ?
              <ShowPopup popup={UploadFilesPopup} parent_id={parent_id} parent_type={uploadParentType} client_id={client_id} resku={false} render={({ onClick }) => {
                return <Button onClick={onClick} style={{ float: 'right' }}>Upload</Button>;
              }} />
              : null)
          }
        </Col>
      </Row>
    );
  }

  renderTabContent() {
    const { job_id, shop_id, client_id, onSelectFile, register, product_images, item_images, autoHideOnError } = this.props;
    const { selectedTab, folder_id } = this.state;
    const exclude_file_id = item_images.length > 0 ? item_images[0].file_id : null;
    const available_product_images = product_images.filter(i => i.file_id !== exclude_file_id);

    let tbd = false;
    if (register !== 'message_file_id' && register !== 'collaborate_file_id' && register !== 'note_file_id') {
      if (selectedTab === 'project' || selectedTab === 'shop' || (selectedTab === 'client' && folder_id === 'all')) {
        tbd = true;
      }
    }

    switch (selectedTab) {
      case 'project':
        return <Files type="project" job_id={job_id} folder_id={folder_id !== 'all' ? folder_id : null} onSelectFile={onSelectFile} exclude_file_ids={exclude_file_id} tbd={tbd} />;
      case 'shop':
        return <Files type="shop" shop_id={shop_id} onSelectFile={onSelectFile} exclude_file_ids={exclude_file_id} tbd={tbd} />;
      case 'client':
        return <Files type="client" client_id={client_id} folder_id={folder_id !== 'all' ? folder_id : null} exclude_file_ids={exclude_file_id} onSelectFile={onSelectFile} tbd={tbd} />;
      default:
        return <Files
          type="item" onSelectFile={onSelectFile} files={available_product_images} tbd={tbd}
          autoHideOnError={(autoHideOnError || []).indexOf('item') > -1}
        />;
    }
  }

  render() {
    const { job_id, shop_id, client_id, onClose, onClosePopup, onSelectFile, resku, index, item_images, parent_id, parent_type, popupClassName = 'reveal large', itemId, itemLocations, popupBaseZIndex } = this.props;
    const { showEditorPopup, showUploadPopup, mockupGeneratorImage } = this.state;
    const isProcessing = this.state.mockupStatus.type === MockupStatus.PROCESSING;
    const { parent_id: mockupParentId, parent_type: mockupParentType } = resolveParentContext({ job_id, shop_id, item_id: itemId });
    const popupZIndex = popupBaseZIndex ?? BASE_ZINDEX;
    const uploadParentType = resolveUploadParentType(parent_type);
    const isJobStyleLayout = ['JOB', 'PURCHASE-ORDER', 'CATALOG'].includes(parent_type);
    const { popupStyle = {
      display: 'block',
      zIndex: popupZIndex + index + 1,
      height: 'initial',
      transition: 'width 0.1s, height 0.1s'
    } } = this.props;

    if (resku) {
      return (<>
        {showEditorPopup && <ImageEditorPopup
          imageSource={getImageSrc(item_images[0].image, 'large')}
          onClose={() => this.setState({ showEditorPopup: false })}
          onSave={onSelectFile}
          parent_id={parent_id}
          parent_type={uploadParentType}
          logo_parent_id={!!job_id ? job_id : (!!shop_id ? shop_id : parent_id)}
          logo_parent_type={!!job_id ? 'JOB' : (!!shop_id ? 'SHOP' : uploadParentType)}
          account_id={client_id}
          account_type="CLIENT"
          overlayZIndex={popupZIndex + index + 1}
          zIndex={popupZIndex + index + 2}
        />}
        {showUploadPopup && <UploadFilesPopup
          onClosePopup={() => this.setState({ showUploadPopup: false })}
          parent_id={parent_id}
          parent_type={uploadParentType}
          client_id={client_id}
          resku={resku}
          web_upload={true}
          index={popupZIndex + index + 1}
        />}
        {mockupGeneratorImage && itemId && this.props.item && <BulkMockupGeneratorPopup
          items={[
            {
              item_id: itemId,
              parent_type: this.props.item.parent_type,
              copied_from: this.props.item.copied_from,
              item_decorations: this.props.item.item_decorations,
              item_images: [
                mockupGeneratorImage,
                ...item_images.filter(ii => ii.item_image_id !== mockupGeneratorImage.item_image_id),
              ],
              item_locations: itemLocations,
            },
          ]}
          accountId={client_id}
          parentId={mockupParentId}
          parentType={mockupParentType}
          overlayZIndex={popupZIndex + index + 1}
          zIndex={popupZIndex + index + 2}
          onClose={() => this.setState({ mockupGeneratorImage: null })}
          onGenerate={this.handleGenerateMockup}
          proofOptions={this.props.proofOptions}
        />}
        <div className={popupClassName} style={popupStyle} data-reveal aria-labelledby="modalTitle" aria-hidden="true" role="dialog">
          <div className="row" id="SelectFileTitle">
            <h3>Select {['ITEM'].includes(parent_type) ? 'Image' : 'File'}</h3>
            <div className="small-12">
              <a className="button" style={{ position: 'fixed', right: '1rem', top: '1rem' }} onClick={e => onClosePopup()}>Close</a>
            </div>
          </div>
          <Theme theme={themeOptions}>
            <div id='scrollableDiv' style={{ overflow: 'auto', height: '100%' }}>
              <div className="row">
                {isProcessing && <div
                  className="small-6 medium-4 large-2 columns end"
                  style={{
                    position: 'relative',
                    backgroundColor: colors.primary1['20'],
                    borderRadius: "5px",
                    padding: "1rem",
                    textAlign: "center",
                    fontSize: "0.8rem",
                    color: colors.primary1.main,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem"
                  }}
                >
                  <SkubotSpinner skubot={false} size='medium' style={{ margin: "auto" }} />
                  <p style={{ marginBottom: '0 !important', fontSize: '1rem' }}>{this.state.mockupStatus.message}</p>
                </div>}
                {item_images.length > 0 && <div
                  key={item_images[0].item_image_id}
                  className="small-6 medium-4 large-2 columns end"
                >
                  <div
                    style={{ position: 'relative' }}
                    onMouseEnter={() => this.setState({ hoverImage: true })}
                    onMouseLeave={() => this.setState({ hoverImage: false })}
                  >
                    <Artwork
                      picture={getImageSrc(item_images[0].image, 'medium')}
                      onEdit={this.canEditImage() ? this.renderImageEditor : undefined}
                    />
                    {!this.props.isPoDShop && this.state.hoverImage && this.props.isPromoStandard && this.props.mockupEnabled && (
                      <Button
                        size="small"
                        disabled={isProcessing}
                        style={{
                          position: 'absolute',
                          top: '2.5rem',
                          right: '0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                        onClick={this.renderMockupGenerator}
                      >
                        <MagicIcon size="small" color="white" />
                        AI Configure
                      </Button>
                    )}
                  </div>
                </div>}
              </div>
              <MockupFeedbackBanner
                status={this.state.mockupStatus}
                onFeedback={this.handleMockupFeedback}
                mockupFeedback={this.state.mockupFeedback}
              />
              <div className="row" style={{ height: isJobStyleLayout ? '500px' : '300px' }}>
                {!isJobStyleLayout && <p>Click images below to replace</p>}
                {this.renderTabs()}
                {this.renderTabContent()}
              </div>
            </div>
          </Theme>
        </div>
      </>);
    }

    return (
      <Theme>
        <Popup
          style={{ padding: '5px 25px' }}
          onClose={onClose}
          title="Select File"
          zIndex={popupZIndex + index + 2}
          overlayZIndex={popupZIndex + index + 1}
          controls={<Button onClick={onClose}>Close</Button>}>
          <div className="row" style={{ overflow: 'auto' }}>
            {this.renderTabs()}
            {this.renderTabContent()}
          </div>
        </Popup>
      </Theme>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  let clientFolders = [{ key: 'all', value: 'All Files' }].concat(getFolderOptions(state).filter(f => f.type === 'CLIENT'));
  let jobFolders = [{ key: 'all', value: 'All Files' }].concat(getFolderOptions(state).filter(f => f.type === 'JOB'));

  const itemId = 'ITEM' === ownProps.parent_type ? ownProps.parent_id : undefined;
  const item = itemId ? getFullItem(state, { item_id: itemId }) : undefined;
  const product = item?.parent_id ? getProduct(state, { product_id: item.parent_id }) : undefined;
  const is3rdPartyDecorator = item?.is_3rd_party_decorator === 1 ?? false;

  const mockupEnabled = aiMockupAndRewriterEnabled(state.identity);

  const psProductId = product?.ext_product_id ?? null;

  const shopId = ownProps.shop_id ?? Object.keys(state.entities.shops || {})[0];
  const shop = shopId ? state.entities.shops?.[shopId] : undefined;
  const isPoDShop = !!shop && isPODShop(shop);

  return {
    identity: state.identity,
    item,
    itemId,
    psProductId,
    is3rdPartyDecorator,
    imprintOptions: getMockupImprintOptions(state),
    job_id: ownProps.job_id ? ownProps.job_id : Object.keys(state.entities.projects || {})[0],
    shop_id: shopId,
    client_id: ownProps.client_id ? ownProps.client_id : Object.values(state.entities.projects || {})[0]?.account_id,
    clientFolders: ownProps.resku ? clientFolders : clientFolders.map(f => ({ value: f.key, label: f.value, type: 'CLIENT' })),
    jobFolders: ownProps.resku ? jobFolders : jobFolders.map(f => ({ value: f.key, label: f.value, type: 'JOB' })),
    item_images: getItemImagesByItemId(state, ownProps),
    product_images: getProductImagesByItemId(state, ownProps),
    isPromoStandard: isPromoStandardItem(item),
    itemLocations: getMockupGeneratorItemLocations(state, item),
    mockupEnabled,
    proofOptions: getProofOptions(state),
    isPoDShop,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const onClosePopup = ownProps.onClosePopup ?? (() => {
    dispatch(closePopup());
  });

  return {
    onClosePopup: onClosePopup,
    // Provide a stable dispatch function for persistDecorationUpdates helper
    dispatchPersistDecorationUpdates: (action) => dispatch(action),
    onFinalizeUploadFile: (file) => dispatch(createFinalizeUploadFile(file)),
    onFinalizeItemImages: (item_image, item_images) => dispatch(createFinalizeAddItemImage(item_image, item_images)),
    onSelectFile: (file) => {
      if (ownProps.onSelectFile != null) {
        ownProps.onSelectFile(file);
        onClosePopup();
        return;
      }

      let { closeOnSelect, resku, parent_type, onClose } = ownProps;
      if (parent_type === 'ITEM') {
        dispatch(createFinalizeAddProductImage(ownProps.parent_id, file.file_id));
      } else if (parent_type === 'PURCHASE-ORDER') {
        dispatch(addProof(ownProps.parent_id, file));
      } else {
        // used in send_email.tpl
        if (ownProps.register === 'user_file_id') {
          var attachments = document.querySelector('input[name=attachment_file_ids]');
          var attachmentList = document.querySelector('div.attachment-list');
          attachments.value = (attachments.value + "," + file.file_id);
          var html = "<p data-file-id='" + file.file_id + "'>" + file.file_display_name + " <a href='#' class='remove-file'>Remove</a></p>";
          attachmentList.insertAdjacentHTML('beforeend', html);
          attachmentList.style.display = 'block';
        } else {
          dispatch(createAddTemp(ownProps.register, file.file_id));
        }
      }

      // temp hack to fix S40-827. Should refactor these logic out of SelectFilePopup
      if (_.isUndefined(closeOnSelect)) {
        if (!resku) {
          closeOnSelect = true;
        } else if (parent_type === 'JOB') {
          onClosePopup();
        }
      }
      if (closeOnSelect) {
        if (onClose) {
          onClose();
        } else {
          onClosePopup();
        }
      }
    },
    onCreateUploadFilesPopup: () => {
      const uploadParentType = resolveUploadParentType(ownProps.parent_type);
      if (ownProps.parent_type === 'ITEM') {
        dispatch(createUploadFilesPopup(ownProps.parent_id, ownProps.parent_type, ownProps.client_id, true, true));
      } else {
        dispatch(createUploadFilesPopup(ownProps.parent_id, uploadParentType, ownProps.client_id));
      }
    },
    onLoadFoldersList: (parent_id, client_id, parent_type = 'JOB') => {
      dispatch(createLoadFoldersList(parent_id, parent_type, client_id));
    },
    onLoadFilesList: (parent_id, parent_type) => {
      dispatch(createLoadFilesList(parent_id, parent_type));
    },
    onLoadOrder: (order_id) => dispatch(createLoadOrder(order_id, true)),
  };
};

const ConnectedSelectFilePopup = connect(mapStateToProps, mapDispatchToProps)(SelectFilePopup);
export default ConnectedSelectFilePopup;
